import { AssetType, MediaProperty } from '@rendernet/api-client';
import { client } from 'api/baseApi.ts';
import { GenerateParamsData } from 'api/interface/GenerateParams.ts';
import GenerateRequest from 'api/request/GenerateRequest';
import { getLatestGenerationYCoordinate } from 'apps/canvas/util/canvasUtil';
import axios from 'axios';
import { APIConfigData } from 'core/config/APIConfigData';
import { TEXT_ASSET_STYLES } from 'core/constants/constants';
import { GenerateResult } from 'core/constants/enums/GenerateResult.ts';
import { GenerationType } from 'core/constants/enums/GenerationType.ts';
import { UploadAssetActions } from 'core/constants/enums/UploadAssetActionsEnum.ts';
import { LocalStorageKeys } from 'core/constants/LocalStorageKeys';
import { CanvasMediaFormatter } from 'core/formatters/CanvasMediaFormatter.ts';
import { StudioMediaFormatter } from 'core/formatters/StudioMediaFormatter.ts';
import apiHandler from '../handlers/apiHandler.ts';
import { costEndpoints } from '../rtk-endpoints/CostEndpoints.ts';
import { mediaEndpoints } from '../rtk-endpoints/MediaEndpoints.ts';
import { voicesEndpoints } from '../rtk-endpoints/VoicesEndpoints.ts';

const formatAssetToCanvasFeedItem = (asset: any) => ({
	id: asset.id,
	assetType: asset.type,
	height: asset?.properties?.scale?.height,
	width: asset?.properties?.scale?.width,
	originalWidth: asset?.properties?.scale?.width,
	originalHeight: asset?.properties?.scale?.height,
	x: asset?.properties?.position?.x,
	y: asset?.properties?.position?.y,
	status: 'success',
	metadata: {
		value: asset?.metadata?.value,
		fontType: asset?.metadata?.font_type,
		fontSize: asset?.metadata?.font_size,
		fontScale: asset?.metadata?.text_scale,
		alignment: asset?.metadata?.alignment,
		visibility: asset?.metadata?.visibility,
		color: asset?.metadata?.color,
	},
	assetId: asset.assetId,
});

export const updateCanvasAssetPropertiesService = async (canvasId, assetPropertiesList) =>
	axios({
		method: 'POST',
		url: `${APIConfigData.RENDERNET_BASE_URL}/v1/canvas/${canvasId}/media`,
		data: {
			media_properties: assetPropertiesList,
		},
	})
		.then(() => assetPropertiesList)
		.catch(() => []);

export const mapAssetResponseDataAndUpdateCanvasProperties = async (
	assetResponseList,
	assetList,
	canvasId,
	isUpdateRequired = true,
) => {
	const assetPropertiesList = assetList?.map((asset, index) => ({
		media_id: assetResponseList[index].id,
		...asset.properties,
	}));
	const assetIdMap = assetResponseList.reduce(
		(acc, asset, index) => ({
			...acc,
			...(asset.id !== assetList[index].shapeId ? { [assetList[index].shapeId]: asset.id } : {}),
		}),
		{},
	);
	const assetDataMap = assetList.reduce(
		(acc, asset, index) => ({
			...acc,
			[asset.shapeId]: formatAssetToCanvasFeedItem({
				id: asset.shapeId,
				assetId: assetResponseList[index].id,
				type: asset.assetType,
				metadata: asset.metadata,
				properties: asset.properties,
			}),
		}),
		{},
	);

	if (!isUpdateRequired) {
		return Promise.resolve({ assetIdMap, assetDataMap });
	}
	return updateCanvasAssetPropertiesService(canvasId, assetPropertiesList).then(() => ({
		assetIdMap,
		assetDataMap,
	}));
};

export const getMediaFeedService = ({
	canvasId,
	newPage,
	pageSize = APIConfigData.DEFAULT_PAGE_SIZE,
	playground = 'studio',
}: {
	canvasId?: string;
	newPage?: number;
	pageSize?: number;
	playground?: string;
}) =>
	new Promise((resolve, reject) => {
		try {
			let feedDataPromise: Promise<any>;
			if (canvasId) {
				feedDataPromise = client.canvas.feed({
					canvas_id: canvasId,
					page: undefined,
					page_size: 500,
				});
			} else {
				feedDataPromise = client.studio.feed({
					page: newPage,
					page_size: pageSize,
				});
			}
			feedDataPromise
				.then((res) => {
					if (canvasId && playground === 'canvas') {
						const { data: history = [] } = res;
						const latestGenerationY = getLatestGenerationYCoordinate(history);

						const latestGenerationX = 20;

						const result = new CanvasMediaFormatter({
							latestGenerationX,
							latestGenerationY,
						}).getCanvasFormattedFeedItems(history);
						resolve(result);
					} else if (playground === 'studio') {
						const { data: history = [], pagination } = res;
						const { total: listSize = pageSize } = pagination;
						const result = new StudioMediaFormatter().getStudioFormattedFeedItems(history);
						const formattedRes = {
							...result,
							listSize,
							pageSize,
						};

						resolve(formattedRes);
					}
				})
				.catch((error) => {
					reject(error);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const generateFreeMediaService = ({ lipSyncOptions, canvasId }: GenerateParamsData) =>
	new Promise((resolve, reject) => {
		const generateRequest = new GenerateRequest({
			lipSyncOptions,
		});
		const requestBody = generateRequest.getGenerateRequestBody();
		try {
			let generatePromise: Promise<any>;
			if (canvasId) {
				generatePromise = client.canvas.generateFreeMedia(
					{
						canvas_id: canvasId,
					},
					requestBody[0],
				);
			} else {
				generatePromise = client.studio.generateFreeMedia(undefined, requestBody[0]);
			}

			generatePromise
				.then((res) => {
					const response = res.data;

					if (response.result === GenerateResult.FAIL) {
						const error = new Error(response.error_code);

						reject(error);
					} else if (canvasId) {
						const latestGenerationX = 20;
						const savedMaxY = Number(localStorage.getItem(LocalStorageKeys.MAX_Y_COORDINATE)) ?? 0;
						const result = new CanvasMediaFormatter({
							latestGenerationX,
							latestGenerationY: savedMaxY,
						}).getCanvasFormattedGeneratedItems(res.data.data, {
							lipSyncOptions,
						});
						resolve({
							formattedDerivedMedia: result.formattedAssets,
						});
					} else {
						const result = new StudioMediaFormatter().getStudioFormattedGeneratedItems(response, {
							lipSyncOptions,
						});
						resolve(result);
					}
				})
				.catch((error) => {
					reject(error);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const generateMediaService = ({
	models,
	styles,
	selectedModels,
	selectedStyles,
	selectedLorasMap,
	prompt,
	negativePrompt,
	samplerInput,
	samplingStepsInput,
	AIGuidanceInput,
	batchSizeInput,
	seedInput,
	aspectRatioInput,
	aspectRatio,
	aspectRatioLabel,
	aspectHeight,
	aspectWidth,
	//
	quality,
	//
	controlNetOptions,
	facelockOptions,
	trueTouchOptions,
	faceSwapOptions,
	lipSyncOptions,
	upscaleOptions,
	//
	character,
	canvasId = '',
	qualityRequestParams,
	changeItOptions,
	videoAnyoneOptions,
	faceReplaceOptions,
	textToVideoOption,
	audioAssetId,
	generationType,
	changeClothingOptions,
	objectPlacementOptions,
	imageReferenceOptions,
}: GenerateParamsData) =>
	new Promise((resolve, reject) => {
		const generateRequest = new GenerateRequest({
			modelsList: models,
			stylesList: styles,
			selectedModels,
			selectedStyles,
			selectedLoRas: selectedLorasMap,
			prompt,
			negativePrompt,
			samplerInput,
			samplingStepsInput,
			aIGuidanceInput: AIGuidanceInput,
			batchSizeInput,
			seedInput,
			aspectRatioLabel,
			quality,
			controlNetOptions,
			facelockOptions,
			trueTouchOptions,
			character,
			faceSwapOptions,
			lipSyncOptions,
			changeItOptions,
			upscaleOptions,
			videoAnyoneOptions,
			faceReplaceOptions,
			textToVideoOption,
			audioAssetId,
			generationType,
			changeClothingOptions,
			objectPlacementOptions,
			imageReferenceOptions,
		});
		const requestBody = generateRequest.getGenerateRequestBody();
		try {
			const generationTypeQueryParams = generationType === GenerationType.MUSIC_VIDEO && {
				type: 'music_video',
			};
			let generatePromise: Promise<any>;
			if (canvasId) {
				generatePromise = client.canvas.generateMedia(
					{
						canvas_id: canvasId,
						...generationTypeQueryParams,
					},
					// @ts-ignore
					requestBody,
				);
			} else {
				// @ts-ignore
				generatePromise = client.studio.generateMedia(
					{
						...generationTypeQueryParams,
						canvas_id: undefined,
					},
					// @ts-ignore
					requestBody,
				);
			}
			// axios({
			// 	method: 'POST',
			// 	url: `${APIConfigData.RENDERNET_BASE_URL}/v1/media/${canvasId ? 'canvas' : 'studio'}/generate`,
			// 	params: {
			// 		...(generationType === GenerationType.MUSIC_VIDEO && { type: 'music_video' }),
			// 		...(canvasId && { canvas_id: canvasId }),
			// 	},
			// 	data: requestBody,
			// 	headers: { 'Content-Type': 'application/json' },
			// })
			generatePromise
				.then((res) => {
					const response = res.data;

					if (response.result === GenerateResult.FAIL) {
						const error = new Error(response.error_code);

						reject(error);
					} else if (canvasId) {
						const latestGenerationX = 20;
						const savedMaxY = Number(localStorage.getItem(LocalStorageKeys.MAX_Y_COORDINATE)) ?? 0;
						const result = new CanvasMediaFormatter({
							latestGenerationX,
							latestGenerationY: savedMaxY,
						}).getCanvasFormattedGeneratedItems(response, {
							selectedLorasMap,
							prompt,
							negativePrompt,
							samplerInput,
							samplingStepsInput,
							AIGuidanceInput,
							batchSizeInput,
							seedInput,
							aspectRatioInput,
							aspectRatio,
							aspectRatioLabel,
							aspectHeight,
							aspectWidth,
							quality,
							controlNetOptions,
							facelockOptions,
							trueTouchOptions,
							faceSwapOptions,
							lipSyncOptions,
							videoAnyoneOptions,
							textToVideoOption,
							character,
							changeClothingOptions,
							objectPlacementOptions,
							imageReferenceOptions,
						});
						resolve({
							formattedDerivedMedia: result.formattedAssets,
						});
					} else {
						const result = new StudioMediaFormatter().getStudioFormattedGeneratedItems(response, {
							prompt,
							negativePrompt,
							samplerInput,
							samplingStepsInput,
							AIGuidanceInput,
							batchSizeInput,
							seedInput,
							aspectRatioInput,
							aspectRatio,
							aspectHeight,
							aspectWidth,
							aspectRatioLabel,
							quality,
							qualityRequestParams,
							controlNetOptions,
							facelockOptions,
							changeItOptions,
							trueTouchOptions,
							lipSyncOptions,
							videoAnyoneOptions,
							textToVideoOption,
							character,
							changeClothingOptions,
							objectPlacementOptions,
							imageReferenceOptions,
						});
						resolve(result);
					}
				})
				.catch((error) => {
					reject(error);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const updateMediaProperties = (canvasId: string, mediaProperties: Array<MediaProperty>) =>
	new Promise((resolve, reject) => {
		try {
			const request = {
				media_properties: mediaProperties,
			};
			client.canvas
				.updateMedia(canvasId, request)
				.then((res) => {
					const mediaData = res.data;
					resolve(mediaData);
				})
				.catch((error) => {
					reject(error);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const getTrueTouchCost = ({ dispatch }) => {
	return apiHandler({
		endpoint: costEndpoints.endpoints.getTrueTouchCost.initiate,
		dispatch,
	});
};

export const getUpscaleCost = ({ dispatch }) => {
	return apiHandler({
		endpoint: costEndpoints.endpoints.getUpscaleCost.initiate,
		dispatch,
	});
};

export const getLipSyncCost = ({ dispatch }) => {
	return apiHandler({
		endpoint: costEndpoints.endpoints.getLipSyncCost.initiate,
		dispatch,
	});
};

export const getFaceReplaceCost = ({ dispatch }) => {
	return apiHandler({
		endpoint: costEndpoints.endpoints.getFaceReplaceCost.initiate,
		dispatch,
	});
};

export const getVideoAnyoneCost = ({ dispatch }) => {
	return apiHandler({
		endpoint: costEndpoints.endpoints.getVideoAnyoneCost.initiate,
		dispatch,
	});
};

export const getMusicVideoCost = ({ dispatch }) => {
	return apiHandler({
		endpoint: costEndpoints.endpoints.getMusicVideoCost.initiate,
		dispatch,
	});
};

export const getGenerationCost = async ({ dispatch }, type: string) => {
	return apiHandler({
		endpoint: costEndpoints.endpoints.getGenerationCost.initiate,
		dispatch,
		args: { generation_type: type },
	});
};

export const updateAsset = async (canvasId: string, assetList: any) => {
	try {
		const assetMetadata = assetList.map(
			(asset: { assetId: any; assetType: any; metadata: any }) => ({
				id: asset.assetId,
				type: asset.assetType,
				metadata: asset.metadata,
			}),
		);
		const response = await client.assets.update(assetMetadata);
		const { data: responseAssetList } = response ?? {};

		if (responseAssetList?.length === assetList?.length) {
			const mappedAssetData = await mapAssetResponseDataAndUpdateCanvasProperties(
				responseAssetList,
				assetList,
				canvasId,
				false,
			);
			return { data: mappedAssetData };
		}
		return { data: {} };
	} catch (error) {
		return { error };
	}
};

export const createAsset = async (canvasId: string, assetList: any) => {
	try {
		const assetMetadata = assetList.map((asset: { assetType: any; metadata: any }) => ({
			type: asset.assetType,
			metadata: asset.metadata,
			tag: 'title',
		}));
		const response = await client.assets.create(assetMetadata);
		const { data: responseAssetList } = response ?? {};

		if (responseAssetList?.length === assetList?.length) {
			const mappedAssetData = await mapAssetResponseDataAndUpdateCanvasProperties(
				responseAssetList,
				assetList,
				canvasId,
				true,
			);
			return { data: mappedAssetData };
		}
		return { data: {} };
	} catch (error) {
		return { error };
	}
};

export const deleteMediaService = (mediaIds: string[]) => {
	return client.media.deleteMultiple({ media_ids: mediaIds }).then((res) => {
		const [{ generation_id: batchId }] = res.data;
		return { mediaIds, batchId };
	});
};

export const getVoicesList = async ({ dispatch }) => {
	return apiHandler({
		endpoint: voicesEndpoints.endpoints.getVoices.initiate,
		dispatch,
	});
};

export const submitMediaFeedback = async (
	mediaId: string,
	comment: string,
	rating: number,
	{ dispatch },
) => {
	const requestBody = {
		comment,
		rating,
	};
	try {
		await apiHandler({
			endpoint: mediaEndpoints.endpoints.submitFeedback.initiate,
			args: { mediaId, feedbackRequest: requestBody },
			dispatch,
		});
		return { data: `${mediaId} feedback submitted` };
	} catch (e) {
		throw new Error(e);
	}
};

const validateCanvasTextAsset = (asset) => {
	const isColorValid =
		TEXT_ASSET_STYLES.color.find((color) => color.value === asset.metadata.color) != null;

	const isFontValid =
		TEXT_ASSET_STYLES.font.find((font) => font.value === asset.metadata.font_type) != null;

	const isFontSizeValid =
		TEXT_ASSET_STYLES.size.find((size) => size.value === asset.metadata.font_size) != null;

	const isTextAlignValid =
		TEXT_ASSET_STYLES.textAlign.find((textAlign) => textAlign.value === asset.metadata.alignment) !=
		null;

	return isColorValid && isFontValid && isFontSizeValid && isTextAlignValid;
};

export const getCanvasAssetsService = (canvasId) =>
	new Promise((resolve, reject) => {
		try {
			client.assets
				.list({
					page_size: 500,
					page: 1,
					type: AssetType.TEXT,
					canvas_id: canvasId,
					tags: ['title', 'comment'].join(','),
				})
				.then((res) => {
					// const { data } = res;
					const { data: assets } = res;
					const formattedAssetsMap = assets.filter(validateCanvasTextAsset).reduce(
						(assetMap, asset) => ({
							...assetMap,
							[asset.id]: formatAssetToCanvasFeedItem({ ...asset, assetId: asset.id }),
						}),
						{},
					);
					resolve(formattedAssetsMap);
				})
				.catch((error) => {
					reject(error);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const uploadMusicVideoCroppedAudioAssetService = async (
	audioBinary: File,
	{
		type: mimeType,
		metadata,
	}: {
		type: string;
		metadata: Record<string, any>;
	},
) => {
	const result = await client.assets.upload(audioBinary, mimeType, {
		type: metadata?.type,
		name: metadata?.name,
		metadata,
		tag: UploadAssetActions.MUSIC_VIDEO,
	});
	const { data } = result ?? {};
	if (!data) throw new Error('Failed to upload audio');
	const { id, url } = data;
	return { assetId: id, assetUrl: url };
};

export const uploadVideoAnyoneImageAssetService = async (imageBinary, { width, height, type }) => {
	const result = await client.assets.upload(imageBinary, type ?? 'image/png', {
		type: AssetType.IMAGE,
		metadata: {
			dim: {
				width,
				height,
			},
			ext: 'png',
		},
		tag: UploadAssetActions.VIDEO_ANYONE,
	});
	const { data } = result ?? {};
	if (!data) throw new Error('Failed to upload image');
	const { id, url } = data;
	return { assetId: id, assetUrl: url };
};

export const removeImageBackground = (inputImageId: string, { dispatch }) => {
	return apiHandler({
		endpoint: mediaEndpoints.endpoints.removeImageBackground.initiate,
		args: { inputImageId },
		dispatch,
	});
};
